import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { searchApi } from '@/api/endpoints/search';
import type { GlobalSearchCategory, GlobalSearchPage } from '@/api/endpoints/search';

/** Min needle length — matches BE `MIN_QUERY_LENGTH` / legacy proc noise floor. */
export const GLOBAL_SEARCH_MIN_QUERY_LENGTH = 2;
/** Max needle length — matches BE `MAX_QUERY_LENGTH` / legacy `NVARCHAR(50)`. */
export const GLOBAL_SEARCH_MAX_QUERY_LENGTH = 50;
/** Default page size — matches BE `DEFAULT_LIMIT`. */
export const GLOBAL_SEARCH_PAGE_SIZE = 30;
/** Debounce before committing typed text to the URL / API. */
export const GLOBAL_SEARCH_DEBOUNCE_MS = 400;

const STALE_TIME_MS = 30_000;

export type UseGlobalSearchParams = {
  q: string;
  category?: GlobalSearchCategory;
  page?: number;
  limit?: number;
  /** Extra gate on top of the min-length rule, e.g. the header preview only while it is open. */
  enabled?: boolean;
};

/**
 * TanStack Query wrapper for `GET /api/search` (port of `ssi_GlobalSearch`).
 * Scoping is enforced server-side; the hook only gates on min query length.
 */
export function useGlobalSearch(params: UseGlobalSearchParams) {
  const q = params.q.trim();
  const category = params.category ?? 'all';
  const page = params.page ?? 1;
  const limit = params.limit ?? GLOBAL_SEARCH_PAGE_SIZE;
  const canSearch = q.length === 0 || q.length >= GLOBAL_SEARCH_MIN_QUERY_LENGTH;

  return useQuery<GlobalSearchPage>({
    queryKey: ['global-search', q, category, page, limit],
    queryFn: () => searchApi.search({ q, category, page, limit }),
    enabled: canSearch && (params.enabled ?? true),
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME_MS,
  });
}
